import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getOrders, updateOrderStatus } from "@/api/endpoints";
import { useI18n } from "@/i18n";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ClipboardList, ChefHat, CheckCircle2, Truck } from "lucide-react";
import type { OrderStatus } from "@/types";

const STATUS_CONFIG: Record<OrderStatus, { labelKey: string; color: string; icon: typeof ClipboardList }> = {
  pending: { labelKey: "pending", color: "bg-yellow-100 text-yellow-800", icon: ClipboardList },
  preparing: { labelKey: "preparing", color: "bg-blue-100 text-blue-800", icon: ChefHat },
  ready: { labelKey: "ready", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  delivered: { labelKey: "delivered", color: "bg-gray-100 text-gray-600", icon: Truck },
};

const NEXT_STATUS: Partial<Record<OrderStatus, OrderStatus>> = {
  pending: "preparing",
  preparing: "ready",
  ready: "delivered",
};

const NEXT_LABEL_KEY: Partial<Record<OrderStatus, string>> = {
  pending: "preparing",
  preparing: "ready",
  ready: "delivered",
};

export function OrdersPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const qc = useQueryClient();
  const { t } = useI18n();

  const timeAgo = (dateStr: string) => {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60) return `${diff}${t("seconds_short")}`;
    if (diff < 3600) return `${Math.floor(diff / 60)}${t("minutes_short")}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}${t("hours_short")}`;
    return `${Math.floor(diff / 86400)}${t("days_short")}`;
  };
  const [statusFilter, setStatusFilter] = useState<string>("active");

  const { data: orders, isLoading } = useQuery({
    queryKey: ["orders", venueId, statusFilter],
    queryFn: () => {
      if (statusFilter === "active") return getOrders(venueId!);
      return getOrders(venueId!, statusFilter);
    },
    enabled: !!venueId,
    refetchInterval: 15000,
  });

  const statusMutation = useMutation({
    mutationFn: ({ orderId, status }: { orderId: string; status: OrderStatus }) =>
      updateOrderStatus(orderId, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["orders", venueId] }),
  });

  const filteredOrders = statusFilter === "active"
    ? orders?.filter((o) => o.status !== "delivered")
    : orders;

  const filters = [
    { id: "active", labelKey: "active" },
    { id: "pending", labelKey: "pending" },
    { id: "preparing", labelKey: "preparing" },
    { id: "ready", labelKey: "ready" },
    { id: "delivered", labelKey: "delivered" },
    { id: "all", labelKey: "all" },
  ];

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold">{t("orders_title")}</h1>

      <div className="mb-6 flex gap-2 overflow-x-auto">
        {filters.map((f) => (
          <Badge
            key={f.id}
            variant={statusFilter === f.id ? "default" : "outline"}
            className="cursor-pointer whitespace-nowrap"
            onClick={() => setStatusFilter(f.id)}
          >
            {t(f.labelKey as Parameters<typeof t>[0])}
          </Badge>
        ))}
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-muted-foreground">{t("loading")}</div>
      ) : !filteredOrders?.length ? (
        <div className="flex flex-col items-center justify-center py-16">
          <ClipboardList className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">{t("no_orders")}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredOrders.map((order, idx) => {
            const id = order.id || order._id || `order-${idx}`;
            const config = STATUS_CONFIG[order.status];
            const nextStatus = NEXT_STATUS[order.status];
            const nextLabelKey = NEXT_LABEL_KEY[order.status];

            return (
              <Card key={id}>
                <CardContent className="pt-5">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold">{order.table}</span>
                      <span className="text-sm text-muted-foreground">{timeAgo(order.createdAt)}</span>
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${config.color}`}>
                      {t(config.labelKey as Parameters<typeof t>[0])}
                    </span>
                  </div>

                  <div className="mb-3 space-y-1">
                    {order.items.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span>
                          {item.quantity}x {item.name}
                          {item.variantName && <span className="text-muted-foreground"> ({item.variantName})</span>}
                        </span>
                        <span className="font-medium">{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                    {order.note && (
                      <p className="mt-1 text-xs italic text-muted-foreground">{t("order_note")}: {order.note}</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between border-t pt-3">
                    <span className="font-bold">{order.totalPrice.toFixed(2)} TRY</span>
                    {nextStatus && nextLabelKey && (
                      <Button
                        size="sm"
                        onClick={() => statusMutation.mutate({ orderId: id, status: nextStatus })}
                        disabled={statusMutation.isPending}
                      >
                        {t(nextLabelKey as Parameters<typeof t>[0])}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
